import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  Clock,
  Edit,
  HelpCircle,
  Pause,
  Play,
  PoundSterling,
  RefreshCw,
  Share2,
  TrendingDown,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  apiResponseToBuilderFormat,
  mapAdjustmentFromGraphql,
} from "@/api/mappers/scenarioAdjustmentMapper";
import {
  mapCommentFromApi,
  mapVersionFromApi,
} from "@/api/mappers/scenarioMapper";
import type { ScenarioAdjustmentViewNode } from "@/api/models/scenarioAdjustment";
import {
  getScenarioAdjustmentsGraphql,
  getScenarioAdjustmentTotals,
} from "@/api/services/scenarioAdjustmentService";
import { getScenarioVersionsGraphql } from "@/api/services/scenarioGraphqlService";
import * as scenarioApi from "@/api/services/scenarioService";
import { getCurrentUser } from "@/api/services/userService";
import { usePermissions } from "@/hooks/usePermissions";
import { useScenarioPermission } from "@/hooks/useScenarioPermission";
import type { ScenarioMetrics } from "@/types/scenarios";
import { useApp } from "../../contexts/AppContext";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useFormatting } from "../../hooks/useFormatting";
import { useScenarioAccess } from "../../hooks/useScenarioAccess";
import {
  mapDemoAuditEntry,
  useScenarioAuditLog,
} from "../../hooks/useScenarioAuditLog";
import { useScenarioForecast } from "../../hooks/useScenarioForecast";
import {
  scenarioIssuesCountQueryKey,
  scenarioIssuesQueryKey,
  useScenarioIssues,
} from "../../hooks/useScenarioIssues";
import { useScenarios } from "../../hooks/useScenarios";
import { useTerminology } from "../../hooks/useTerminology";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";
import { normalizeAdjustment } from "../../lib/normalizeAdjustment";
import { formatCoverageValue } from "../../lib/scenarioEngine";
import { formatDate } from "../../lib/utils";
import type { ScenarioChange } from "../../types";
import type { ScenarioIssue, ScenarioVersion } from "../../types/scenarios";
import {
  ParkScenarioModal,
  ShareScenarioModal,
} from "../modals/ScenarioModals";
import { PermissionGate } from "../permissions/PermissionGate";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { BusinessEventsModal } from "./BusinessEventsModal";
import { ScenarioBuilder } from "./builder/ScenarioBuilder";
import { ChatDiscussion } from "./ChatDiscussion";
import { DependenciesTab } from "./DependenciesTab";
import { QuickAdjustmentsModal } from "./QuickAdjustmentsModal";
import { RestoreVersionDialog } from "./RestoreVersionDialog";
import { ScenarioAuditLogTab } from "./ScenarioAuditLogTab";
import {
  resolveFreshness,
  ScenarioFreshnessBadge,
} from "./ScenarioFreshnessBadge";
import { ScenarioIssuesTab } from "./ScenarioIssuesTab";
import {
  ScenarioDetailSkeleton,
  ScenarioMetricsSkeleton,
} from "./ScenarioSkeletons";
import { ScenarioWorkflowActions } from "./ScenarioWorkflowActions";
import { VersionViewerModal } from "./VersionViewerModal";
import { ViewApproversModal } from "./ViewApproversModal";

interface ScenarioDetailProps {
  scenarioId: string;
  onBack: () => void;
  onEdit?: () => void;
  initialTab?: string;
}

// Helper function to get initials from name
const getInitials = (name: string): string => {
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to get consistent color for user
const getUserColor = (userId: string): string => {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
    "bg-red-500",
  ];

  // Simple hash function to get consistent color for user
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

type ScenarioCategory = "growth" | "cost-cutting" | "crisis" | "custom";

// User-selectable informational tags (manual only — no automated rules).
// 'custom' ("Not Set") is selectable so a tag can be cleared back to untagged.
const CATEGORY_OPTIONS: ScenarioCategory[] = [
  "growth",
  "cost-cutting",
  "crisis",
  "custom",
];

const CATEGORY_LABELS: Record<ScenarioCategory, string> = {
  growth: "Growth",
  "cost-cutting": "Cost-Cutting",
  crisis: "Crisis",
  custom: "Not Set",
};

// Badge styling mirrors ScenarioCard category colors for consistency.
const CATEGORY_BADGE_CLASSES: Record<ScenarioCategory, string> = {
  growth:
    "bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-500)]",
  "cost-cutting":
    "bg-[var(--accent-info-50)] text-[var(--accent-info-900)] border-[var(--accent-info-500)]",
  crisis:
    "bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-500)]",
  custom:
    "bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-500)]",
};

export const ScenarioDetailFull: React.FC<ScenarioDetailProps> = ({
  scenarioId,
  onBack,
  onEdit,
  initialTab = "builder",
}) => {
  const { hasPermission } = usePermissions();
  const {
    addComment,
    replyToComment,
    reactToComment,
    updateScenario,
    createVersion,
    removeShare,
    refreshScenario,
  } = useScenarios();
  const scenarios = useVisibleScenarios();
  const { currentUser, mode } = useApp();
  const { formatCurrency } = useFormatting();
  const queryClient = useQueryClient();
  const { data: currentUserData } = useQuery({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    enabled: mode === "live",
    staleTime: 5 * 60 * 1000,
  });

  const { data: commentsData } = useQuery({
    queryKey: ["scenarioComments", Number(scenarioId)],
    queryFn: () => scenarioApi.getScenarioComments(Number(scenarioId)),
    enabled: mode === "live" && !!scenarioId,
    staleTime: 30_000,
  });

  const { data: adjustmentTotals, isPending: adjustmentTotalsPending } =
    useQuery({
      queryKey: ["scenarioAdjustmentTotals", Number(scenarioId)],
      queryFn: () => getScenarioAdjustmentTotals(Number(scenarioId)),
      enabled: mode === "live" && !!scenarioId,
      staleTime: 30_000,
    });

  const { data: liveAdjustmentsData, isPending: liveAdjustmentsPending } =
    useQuery({
      queryKey: ["scenarioAdjustmentsGraphql", Number(scenarioId)],
      queryFn: () => getScenarioAdjustmentsGraphql(Number(scenarioId), 200),
      enabled: mode === "live" && !!scenarioId,
      staleTime: 30_000,
    });

  const liveAdjustmentNodes = useMemo((): ScenarioAdjustmentViewNode[] => {
    if (!liveAdjustmentsData) return [];
    return liveAdjustmentsData.edges.map((e) => e.node);
  }, [liveAdjustmentsData]);

  const liveAdjustments = useMemo(() => {
    return liveAdjustmentNodes.map((node) => {
      const mapped = mapAdjustmentFromGraphql(node);
      const builderFormat = apiResponseToBuilderFormat(
        {
          id: mapped.id,
          scenarioId: mapped.scenarioId,
          adjustmentType: mapped.adjustmentType,
          adjustmentDirection: mapped.adjustmentDirection,
          startDate: mapped.startDate,
          endDate: mapped.endDate,
          frequency: mapped.frequency,
          confidenceLevel: mapped.confidenceLevel,
          description: mapped.description,
          additionalComments: mapped.additionalComments,
          adjustmentValuePercentage: mapped.adjustmentValuePercentage,
          adjustedValue: mapped.adjustedValue,
          adjustedDueDate: mapped.adjustedDueDate,
          linkedTransactionId: mapped.linkedTransactionId,
          transactionType: mapped.transactionType,
          monthEndHandling: mapped.monthEndHandling,
          linkedAccountName: node.linkedAccountName,
          linkedTransactionSource: node.linkedTransactionSource,
          linkedTransactionAmount: node.linkedTransactionAmount,
          linkedTransactionDueDate: node.linkedTransactionDueDate,
          linkedTransactionStatus: node.linkedTransactionStatus,
          createdByUserId: mapped.createdByUserId,
          createdByUserName: mapped.createdByUserName,
          dateCreated: mapped.dateCreated,
          dateModified: mapped.dateModified,
        },
        mapped.netCashImpact,
      );
      return normalizeAdjustment(builderFormat);
    });
  }, [liveAdjustmentNodes]);

  const liveComments = useMemo(
    () => (commentsData?.comments ?? []).map(mapCommentFromApi),
    [commentsData],
  );

  const { data: versionNodes } = useQuery({
    queryKey: ["scenarioVersionsGraphql", Number(scenarioId)],
    queryFn: () => getScenarioVersionsGraphql(Number(scenarioId)),
    enabled: mode === "live" && !!scenarioId,
    staleTime: 30_000,
  });
  const liveVersions = useMemo(
    () =>
      (versionNodes ?? []).map((node) =>
        mapVersionFromApi({
          ...node,
          description: node.description ?? "",
          createdByUserName: node.createdByUserName ?? "",
        }),
      ),
    [versionNodes],
  );
  // currentUser is already available from useApp() above
  const scenario = scenarios.find((s) => s.id === scenarioId);
  const { isOwner, canEdit, canComment } = useScenarioPermission(scenario);
  const scenarioAccess = useScenarioAccess(scenario);

  // Track whether we previously had a valid scenario (to distinguish "never loaded" from "lost access")
  const hadScenario = useRef(false);
  // Grace period: avoid flashing "Scenario not found" while context propagates
  // (e.g. after creation in live mode, the scenarios query refetch may take a few seconds)
  const [graceExpired, setGraceExpired] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setGraceExpired(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  // Cancel grace early once scenario loads
  useEffect(() => {
    if (scenario) {
      setGraceExpired(true);
    }
  }, [scenario]);
  useEffect(() => {
    if (scenario) {
      hadScenario.current = true;
    } else if (hadScenario.current) {
      // Scenario was previously visible but is no longer accessible — navigate back
      toast.success("Returned to scenarios", {
        description: "You no longer have access to view this scenario.",
      });
      onBack();
    }
  }, [scenario, onBack, toast]);

  // Debug: Log scenario changes
  console.log(
    "ScenarioDetailFull - Scenario:",
    scenario?.name,
    "Changes count:",
    scenario?.changes?.length,
  );

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [parkModalOpen, setParkModalOpen] = useState(false);
  const [viewApproversOpen, setViewApproversOpen] = useState(false);

  const [quickAdjustmentsModalOpen, setQuickAdjustmentsModalOpen] =
    useState(false);
  const [businessEventsModalOpen, setBusinessEventsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [adjustmentCount, setAdjustmentCount] = useState(0);

  // Issues — sourced from GraphQL (vw_ScenarioIssues) in live mode, from the
  // in-memory scenario.issues in demo mode. Local state so dismiss/fix updates
  // are reflected immediately without a full context write.
  const { issues: liveIssues } = useScenarioIssues(
    scenarioId ? Number(scenarioId) : undefined,
  );
  const [localIssues, setLocalIssues] = useState<ScenarioIssue[]>(
    () => scenario?.issues ?? [],
  );

  useEffect(() => {
    setLocalIssues(mode === "live" ? liveIssues : (scenario?.issues ?? []));
  }, [mode, liveIssues, scenario?.issues]);

  // A scenario refresh runs asynchronously server-side: the POST only enqueues the
  // work, and a background handler then re-materialises the scenario and resets +
  // re-detects its issues. The POST promise therefore resolves BEFORE detection has
  // run, so invalidating on it refetches an empty/stale set. Instead, refetch once
  // freshness leaves "updating" (i.e. the refresh has completed) so the Issues tab and
  // its count reflect the freshly detected set, and refresh the transactions table too.
  const liveFreshness = scenario ? resolveFreshness(scenario) : undefined;
  const prevFreshnessRef = useRef(liveFreshness);
  useEffect(() => {
    const previous = prevFreshnessRef.current;
    prevFreshnessRef.current = liveFreshness;
    if (mode !== "live" || !scenarioId) return;
    if (
      previous === "updating" &&
      liveFreshness &&
      liveFreshness !== "updating"
    ) {
      const numericId = Number(scenarioId);
      queryClient.invalidateQueries({
        queryKey: scenarioIssuesQueryKey(numericId),
      });
      queryClient.invalidateQueries({
        queryKey: scenarioIssuesCountQueryKey(numericId),
      });
      // The refresh and any issues it raises are audited server-side as part of
      // the same background run, so the Audit Log tab is stale for them too.
      queryClient.invalidateQueries({
        queryKey: ["scenarioAuditLog", numericId],
      });
      setTableRefreshSignal((s) => s + 1);
    }
  }, [liveFreshness, mode, scenarioId, queryClient]);

  const activeIssueCount = localIssues.filter((i) => !i.resolution).length;

  // Audit log — GraphQL (vw_ScenarioAuditLog) in live mode, the in-memory demo
  // auditLog built by appendAuditEntry() otherwise.
  const { entries: liveAuditEntries, isLoading: auditLogLoading } =
    useScenarioAuditLog(scenarioId ? Number(scenarioId) : undefined);
  const auditEntries = useMemo(
    () =>
      mode === "live"
        ? liveAuditEntries
        : (scenario?.auditLog ?? []).map(mapDemoAuditEntry),
    [mode, liveAuditEntries, scenario?.auditLog],
  );

  const {
    cashDurationShortTerm,
    getActionsTerm,
    getCashDurationTerm,
    getCashDurationShortTerm,
    getScenarioTerm,
  } = useTerminology();
  const [viewingVersion, setViewingVersion] = useState<ScenarioVersion | null>(
    null,
  );
  const [restoringVersion, setRestoringVersion] =
    useState<ScenarioVersion | null>(null);
  const [shareToRemove, setShareToRemove] = useState<{
    userId: string;
    displayName: string;
  } | null>(null);
  const [liveMetrics, setLiveMetrics] = useState<ScenarioMetrics | null>(null);
  // Bumped once a scenario refresh completes so the transactions table refetches
  // (filters intact) alongside the chart/impact query invalidations.
  const [tableRefreshSignal, setTableRefreshSignal] = useState(0);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");

  // Server-side scenario forecast (live mode) — provides authoritative metrics
  const { data: serverForecast, isPending: serverForecastPending } =
    useScenarioForecast(scenarioId, "90d");

  // Helper function to get metric name from terminology
  const getMetricName = (): string => {
    const shortTerm = getCashDurationShortTerm();
    return shortTerm.charAt(0).toUpperCase() + shortTerm.slice(1);
  };

  // Helper function to get metric unit from terminology
  const getMetricUnit = (): string => {
    if (cashDurationShortTerm === "cash until") return "";
    return "months";
  };

  // In live mode, whether the scenario has zero adjustments (should mirror base forecast)
  const hasNoAdjustments =
    mode === "live" &&
    adjustmentTotals != null &&
    adjustmentTotals.revenueCount === 0 &&
    adjustmentTotals.costCount === 0;

  // Calculate metrics from adjustments
  // Priority in live mode: server forecast metrics (authoritative) > live builder metrics > scenario context.
  // In demo mode: live builder metrics > scenario context.
  // When the scenario has zero adjustments, impact is zero by definition.
  const calculatedMetrics = useMemo(() => {
    if (hasNoAdjustments) {
      return {
        netImpact: 0,
        revenueChangeAmount: 0,
        costChangeAmount: 0,
        scenarioRunway: 0,
        isPositiveCashFlow: true,
        scenarioCash: 0,
        minCash: 0,
        minCashDate: new Date().toISOString(),
      };
    }
    const serverMetrics = serverForecast?.metrics ?? null;
    // In live mode, server metrics are authoritative (especially for % adjustments
    // where the server knows actual base transaction amounts).
    const m =
      mode === "live" && serverMetrics
        ? serverMetrics
        : (liveMetrics ?? serverMetrics ?? scenario?.metrics);
    if (!m) {
      return {
        netImpact: 0,
        revenueChangeAmount: 0,
        costChangeAmount: 0,
        scenarioRunway: 0,
        isPositiveCashFlow: false,
        scenarioCash: 0,
        minCash: 0,
        minCashDate: new Date().toISOString(),
      };
    }
    const runway = m.runway ?? 0;
    const isPositiveCashFlow = runway === Infinity || m.netImpact > 50000;
    return {
      netImpact: m.netImpact,
      revenueChangeAmount: m.revenueChangeAmount,
      costChangeAmount: m.costChangeAmount,
      scenarioRunway: runway,
      isPositiveCashFlow,
      scenarioCash: m.endingCash,
      minCash: m.minCash,
      minCashDate:
        m.minCashDate instanceof Date
          ? m.minCashDate.toISOString()
          : new Date(m.minCashDate).toISOString(),
    };
  }, [scenario?.metrics, liveMetrics, serverForecast, hasNoAdjustments, mode]);

  // Base forecast for comparison rows in metric cards
  const {
    baseForecast: baseForecastData,
    isLoading: baseForecastLoading,
    forecastNotReady,
  } = useBaseForecast();

  // Live mode only: hold the metric cards back until every numeric input has
  // settled, so figures never render part-computed. Demo mode is synchronous —
  // all of these queries are disabled there, so this is always false.
  const liveQueriesEnabled = mode === "live" && !!scenarioId;
  const metricsLoading =
    mode === "live" &&
    (baseForecastLoading ||
      (liveQueriesEnabled &&
        !Number.isNaN(Number(scenarioId)) &&
        serverForecastPending) ||
      (liveQueriesEnabled &&
        (adjustmentTotalsPending || liveAdjustmentsPending)));
  const baseForecast = useMemo((): {
    closingCash: number;
    runway: number;
    minCash: number;
  } | null => {
    // In live mode with server forecast, derive base closing balance from the
    // last projected forecast point's baseBalance for consistency with server metrics.
    if (mode === "live" && serverForecast?.projectedForecast?.length) {
      const points = serverForecast.projectedForecast;
      const lastPoint = points[points.length - 1];
      const first = baseForecastData[0];
      let minBal = lastPoint.baseBalance;
      for (const p of points) {
        if (p.baseBalance < minBal) minBal = p.baseBalance;
      }
      let totalIn = 0;
      let totalOut = 0;
      for (const p of baseForecastData) {
        totalIn += p.incoming;
        totalOut += p.outgoing;
      }
      const avgDailyBurn =
        baseForecastData.length > 0
          ? (totalOut - totalIn) / baseForecastData.length
          : 0;
      const runway =
        avgDailyBurn > 0 ? (first?.balance ?? 0) / avgDailyBurn / 30.44 : 18;
      return {
        closingCash: lastPoint.baseBalance,
        runway: Math.round(runway * 10) / 10,
        minCash: Math.round(minBal),
      };
    }

    // Demo mode / fallback: use baseForecastData closing balance (last data point)
    if (baseForecastData.length === 0) return null;
    const first = baseForecastData[0];
    const last = baseForecastData[baseForecastData.length - 1];
    let minBal = first?.balance ?? 0;
    let totalIn = 0;
    let totalOut = 0;
    for (const p of baseForecastData) {
      if (p.balance < minBal) minBal = p.balance;
      totalIn += p.incoming;
      totalOut += p.outgoing;
    }
    const avgDailyBurn = (totalOut - totalIn) / baseForecastData.length;
    const runway =
      avgDailyBurn > 0 ? (first?.balance ?? 0) / avgDailyBurn / 30.44 : 18;
    return {
      closingCash: last?.balance ?? 0,
      runway: Math.round(runway * 10) / 10,
      minCash: Math.round(minBal),
    };
  }, [baseForecastData, serverForecast, mode]);

  if (!scenario) {
    if (!graceExpired && !hadScenario.current) {
      return (
        <div className="bg-background overflow-auto">
          <ScenarioDetailSkeleton
            metricLabels={[
              "Cash Position",
              getMetricName(),
              "Net Impact",
              "Min Cash",
            ]}
          />
        </div>
      );
    }
    return (
      <div className="bg-background overflow-auto">
        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <Button variant="ghost" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <p className="text-muted-foreground mt-2">Scenario not found</p>
          </div>
        </div>
      </div>
    );
  }

  // Derived freshness for the header badge + Refresh button gating.
  const detailFreshness = resolveFreshness(scenario);

  const handlePostComment = (text: string) => {
    if (text.trim()) {
      addComment(scenarioId, text);
    }
  };

  const handleSaveVersion = () => {
    if (!scenario) return;
    createVersion(scenario.id, "Manual version snapshot");
    toast.success("Version Saved", {
      description: "A snapshot of the current adjustments was saved.",
    });
  };

  const handleRestoreVersion = async (version: ScenarioVersion) => {
    if (!scenario) return;

    // Demo mode: restore adjustments in-memory from the version's snapshot.
    if (mode !== "live") {
      const restoredChanges = version.data?.changes;
      if (!restoredChanges || restoredChanges.length === 0) {
        toast.error("Cannot Restore", {
          description: `Version ${version.version} has no saved adjustments to restore.`,
        });
        setRestoringVersion(null);
        return;
      }
      updateScenario(scenario.id, { changes: restoredChanges });
      setRestoringVersion(null);
      toast.success("Version Restored", {
        description: `Adjustments restored to v${version.version}.`,
      });
      setActiveTab("builder");
      return;
    }

    // Live mode: permanently overwrite the scenario's adjustments from the
    // selected version's snapshot via the REST restore endpoint, then refetch.
    const numericId = Number(scenario.id);
    const versionId = Number(version.id);
    try {
      await scenarioApi.restoreScenarioVersion(numericId, versionId);
      queryClient.invalidateQueries({ queryKey: ["scenarios"] });
      queryClient.invalidateQueries({ queryKey: ["scenarioViews"] });
      queryClient.invalidateQueries({
        queryKey: ["scenarioVersionsGraphql", numericId],
      });
      queryClient.invalidateQueries({
        queryKey: ["scenarioAdjustmentsGraphql", numericId],
      });
      queryClient.invalidateQueries({
        queryKey: ["scenarioAdjustmentTotals", numericId],
      });
      queryClient.invalidateQueries({
        queryKey: ["scenario-forecast", numericId],
      });
      queryClient.invalidateQueries({
        queryKey: ["scenarioAuditLog", numericId],
      });

      setRestoringVersion(null);
      toast.success("Version Restored", {
        description: `Scenario adjustments restored to v${version.version}.`,
      });
      setActiveTab("builder");
    } catch (error) {
      console.error("Failed to restore version:", error);
      setRestoringVersion(null);
      toast.error("Restore Failed", {
        description: "Could not restore this version. Please try again.",
      });
    }
  };

  const handleUpdate = (updates: Partial<typeof scenario>) => {
    if (!scenario) return;
    updateScenario(scenario.id, updates);
  };

  const canEditName = isOwner && scenario?.status === "draft";

  const commitNameEdit = () => {
    if (!isEditingName || !scenario) return;
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === scenario.name) {
      setIsEditingName(false);
      return;
    }
    handleUpdate({ name: trimmed });
    setIsEditingName(false);
  };

  const canEditDescription = canEditName;

  const canEditCategory = canEdit && scenario?.status === "draft";

  const commitDescriptionEdit = () => {
    if (!isEditingDescription || !scenario) return;
    const trimmed = descriptionDraft.trim();
    if (trimmed === (scenario.description ?? "").trim()) {
      setIsEditingDescription(false);
      return;
    }
    handleUpdate({ description: trimmed });
    setIsEditingDescription(false);
  };

  const handleBackWithCheck = () => {
    onBack();
  };

  return (
    <div className="bg-background">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <Button
              variant="ghost"
              onClick={handleBackWithCheck}
              className="shrink-0 mt-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex-1 min-w-0">
              {/* Title row */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {isEditingName ? (
                  <Input
                    autoFocus
                    value={nameDraft}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        commitNameEdit();
                      } else if (e.key === "Escape") {
                        setIsEditingName(false);
                      }
                    }}
                    onBlur={commitNameEdit}
                    className="h-auto w-auto min-w-[16rem] max-w-full text-2xl font-bold text-neutral-900"
                  />
                ) : canEditName ? (
                  <h1 className="text-2xl font-bold text-neutral-900">
                    <button
                      type="button"
                      className="text-left cursor-text rounded px-1 -mx-1 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => {
                        setNameDraft(scenario.name);
                        setIsEditingName(true);
                      }}
                      title="Click to edit name"
                    >
                      {scenario.name}
                    </button>
                  </h1>
                ) : (
                  <h1 className="text-2xl font-bold text-neutral-900">
                    {scenario.name}
                  </h1>
                )}
                {scenario.label && (
                  <Badge variant="secondary">{scenario.label}</Badge>
                )}
                <Badge
                  variant={
                    scenario.status === "active" ||
                    scenario.status === "committed"
                      ? "success"
                      : scenario.status === "pending-approval"
                        ? "warning"
                        : scenario.status === "rejected"
                          ? "destructive"
                          : "secondary"
                  }
                >
                  {scenario.status === "pending-approval"
                    ? "Pending Approval"
                    : scenario.status
                        .replace(/-/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                </Badge>
                <ScenarioFreshnessBadge scenario={scenario} />
                {(() => {
                  const category: ScenarioCategory =
                    scenario.category ?? "custom";
                  const badgeClass = CATEGORY_BADGE_CLASSES[category];
                  if (!canEditCategory) {
                    return (
                      <Badge className={badgeClass} variant="outline">
                        {CATEGORY_LABELS[category]}
                      </Badge>
                    );
                  }
                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Change scenario tag"
                          className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${badgeClass}`}
                        >
                          {CATEGORY_LABELS[category]}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {CATEGORY_OPTIONS.map((option) => (
                          <DropdownMenuItem
                            key={option}
                            onSelect={() => handleUpdate({ category: option })}
                          >
                            {CATEGORY_LABELS[option]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })()}
              </div>
              {/* Description */}
              {isEditingDescription ? (
                <Textarea
                  autoFocus
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      commitDescriptionEdit();
                    } else if (e.key === "Escape") {
                      setIsEditingDescription(false);
                    }
                  }}
                  onBlur={commitDescriptionEdit}
                  className="mb-3 text-sm text-muted-foreground"
                />
              ) : canEditDescription ? (
                <p className="text-sm text-muted-foreground mb-3">
                  <button
                    type="button"
                    className="text-left cursor-text rounded px-1 -mx-1 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => {
                      setDescriptionDraft(scenario.description ?? "");
                      setIsEditingDescription(true);
                    }}
                    title="Click to edit description"
                  >
                    {scenario.description || "Add a description…"}
                  </button>
                </p>
              ) : (
                scenario.description && (
                  <p className="text-sm text-muted-foreground mb-3">
                    {scenario.description}
                  </p>
                )
              )}
              {/* Meta row */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium text-neutral-700">Created</span>
                  <span>{scenario.createdByName}</span>
                  <span>·</span>
                  <span>{formatDate(scenario.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium text-neutral-700">
                    Last modified
                  </span>
                  <span>{scenario.modifiedBy || scenario.createdByName}</span>
                  <span>·</span>
                  <span>{formatDate(scenario.modifiedAt)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center shrink-0">
            {/* Workflow actions — permission-aware, status-driven */}
            <ScenarioWorkflowActions
              scenario={scenario}
              variant="detail"
              onStatusChanged={onBack}
              adjustmentCountOverride={
                mode === "live" && liveAdjustmentsData
                  ? liveAdjustmentNodes.length
                  : undefined
              }
            />

            {/* Edit — non-builder tabs */}
            {onEdit && canEdit && activeTab !== "builder" && (
              <Button variant="outline" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}

            {/* Share — owner only */}
            {isOwner && (
              <PermissionGate permission="create_scenarios">
                <Button
                  variant="outline"
                  onClick={() => setShareModalOpen(true)}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </PermissionGate>
            )}

            {/* Refresh — re-pin to the latest base forecast (owner/edit only) */}
            {canEdit &&
              scenario.status !== "parked" &&
              scenario.status !== "archived" && (
                <Button
                  variant="outline"
                  disabled={detailFreshness === "updating"}
                  onClick={() => {
                    const result = refreshScenario(scenario.id);
                    // Once the live refresh + re-materialisation
                    // completes, refetch the transactions table so it
                    // isn't left showing stale rows.
                    Promise.resolve(result).then(() => {
                      setTableRefreshSignal((s) => s + 1);
                      // Issues are reset + re-detected server-side on
                      // refresh; refetch so the tab reflects the fresh set.
                      queryClient.invalidateQueries({
                        queryKey: scenarioIssuesQueryKey(Number(scenario.id)),
                      });
                      queryClient.invalidateQueries({
                        queryKey: scenarioIssuesCountQueryKey(
                          Number(scenario.id),
                        ),
                      });
                    });
                    toast.success("Refreshing scenario", {
                      description: `"${scenario.name}" is being re-pinned to the latest base forecast.`,
                    });
                  }}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${detailFreshness === "updating" ? "animate-spin" : ""}`}
                  />
                  {detailFreshness === "updating" ? "Refreshing…" : "Refresh"}
                </Button>
              )}

            {/* Park / Reactivate — edit or higher */}
            {canEdit && scenario.status !== "parked" && (
              <PermissionGate permission="create_scenarios">
                <Button
                  variant="outline"
                  onClick={() => setParkModalOpen(true)}
                >
                  <Pause className="h-4 w-4 mr-2" />
                  Park
                </Button>
              </PermissionGate>
            )}
            {canEdit && scenario.status === "parked" && (
              <Button variant="outline">
                <Play className="h-4 w-4 mr-2" />
                Reactivate
              </Button>
            )}
          </div>
        </div>

        {/* Approval Workflow Stage Bar — full width, detail view only */}
        {scenario.status === "pending-approval" &&
          scenario.currentStageName && (
            <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                    Awaiting Approval
                  </span>
                </div>
                <span className="text-amber-400 text-xs">|</span>
                {scenario.approvalWorkflowName && (
                  <span className="text-sm font-semibold text-amber-900">
                    {scenario.approvalWorkflowName}
                  </span>
                )}
                {scenario.approvalWorkflowName && scenario.currentStageName && (
                  <span className="text-amber-400 text-xs">·</span>
                )}
                {scenario.currentStageName && (
                  <span className="text-sm text-amber-800">
                    {scenario.currentStageName}
                  </span>
                )}
                {scenario.currentStageOrder && (
                  <>
                    <span className="text-amber-400 text-xs">·</span>
                    <span className="text-sm text-amber-700">
                      Step {scenario.currentStageOrder} of{" "}
                      {scenario.approvalChain?.length ??
                        scenario.currentStageOrder}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setViewApproversOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 hover:text-amber-900 hover:underline shrink-0"
              >
                <Users className="h-3.5 w-3.5" />
                View Approvers
              </button>
            </div>
          )}

        {/* Permission Banner */}
        {scenario.currentUserPermission && !isOwner && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
            <span>
              You have <strong>{scenario.currentUserPermission}</strong> access
              to this scenario.
            </span>
          </div>
        )}

        {/* Metrics Cards */}
        {(() => {
          // In live mode the scenario's content is its adjustments (not the
          // legacy `changes` array, which the adjustment-based builder never
          // populates). Gate the metric cards on adjustment presence so cloned
          // and freshly-built live scenarios render their computed metrics.
          const hasChanges =
            mode === "live"
              ? liveAdjustmentNodes.length > 0 ||
                (adjustmentTotals != null &&
                  adjustmentTotals.revenueCount + adjustmentTotals.costCount >
                    0)
              : scenario.changes && scenario.changes.length > 0;
          const baseUnavailable = !baseForecast;

          if (metricsLoading) {
            return (
              <ScenarioMetricsSkeleton
                labels={[
                  "Cash Position",
                  getMetricName(),
                  "Net Impact",
                  "Min Cash",
                ]}
              />
            );
          }

          if (baseUnavailable) {
            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  "Cash Position",
                  getMetricName(),
                  "Net Impact",
                  "Min Cash",
                ].map((label) => (
                  <Card
                    key={label}
                    style={{ borderLeft: "3px solid var(--mc-info-border)" }}
                  >
                    <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
                      <span className="text-base font-medium text-neutral-800">
                        {label}
                      </span>
                      <div className="text-2xl font-bold text-muted-foreground">
                        —
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {forecastNotReady
                          ? "Run a base forecast first"
                          : "Base forecast not available"}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          }

          const cashChange = hasChanges
            ? calculatedMetrics.scenarioCash - baseForecast.closingCash
            : 0;
          const minCashChange = hasChanges
            ? Math.round(
                (calculatedMetrics.minCash - baseForecast.minCash) / 1000,
              ) * 1000
            : 0;
          const runwayChange = hasChanges
            ? Math.round(
                (calculatedMetrics.scenarioRunway - baseForecast.runway) * 10,
              ) / 10
            : 0;
          const isPositiveCashFlow = calculatedMetrics.isPositiveCashFlow;

          return (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Cash Position */}
              <Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
                <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-medium text-neutral-800">
                        Cash Position
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-64">
                            <p className="font-semibold mb-1">Cash Position</p>
                            <p className="text-xs">
                              Your projected ending cash balance once all
                              scenario adjustments are applied. Compared against
                              the base forecast to show the overall cash impact.
                            </p>
                            <p className="text-xs mt-1 text-muted-foreground">
                              Calculation: Current cash + net effect of all
                              revenue and cost changes in this scenario.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--mc-info-bg)" }}
                    >
                      <PoundSterling
                        className="h-4 w-4"
                        style={{ color: "var(--mc-info-icon)" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                      {!hasChanges ? (
                        <span className="text-muted-foreground text-base">
                          —
                        </span>
                      ) : (
                        formatCurrency(calculatedMetrics.scenarioCash, "full")
                      )}
                    </div>
                    {hasChanges && (
                      <div className="text-xs text-neutral-500 mt-0.5">
                        Base: {formatCurrency(baseForecast.closingCash, "full")}
                      </div>
                    )}
                  </div>
                  {hasChanges && (
                    <div className="flex items-center gap-1 pt-2 border-t">
                      {cashChange >= 0 ? (
                        <TrendingUp
                          className="w-4 h-4"
                          style={{ color: "var(--mc-success-icon)" }}
                        />
                      ) : (
                        <TrendingDown
                          className="w-4 h-4"
                          style={{ color: "var(--mc-warning-icon)" }}
                        />
                      )}
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color:
                            cashChange >= 0
                              ? "var(--mc-success-icon)"
                              : "var(--mc-warning-icon)",
                        }}
                      >
                        {cashChange >= 0 ? "+" : ""}
                        {formatCurrency(cashChange, "full")}
                      </span>
                      {baseForecast.closingCash !== 0 && (
                        <span className="text-xs text-neutral-500">
                          ({cashChange >= 0 ? "+" : ""}
                          {(
                            (cashChange / baseForecast.closingCash) *
                            100
                          ).toFixed(1)}
                          %)
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Coverage / Runway */}
              <Card
                style={{ borderLeft: "3px solid var(--mc-success-border)" }}
              >
                <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-medium text-neutral-800">
                        {getMetricName()}
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-64">
                            <p className="font-semibold mb-1">Cash Coverage</p>
                            <p className="text-xs">
                              How many months the business can sustain
                              operations at the current net burn rate, with this
                              scenario's adjustments applied.
                            </p>
                            <p className="text-xs mt-1 text-muted-foreground">
                              Calculation: Projected cash balance ÷ average
                              monthly net outflow. Shows "Positive Cash Flow"
                              when income exceeds expenditure.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--mc-success-bg)" }}
                    >
                      <Clock
                        className="h-4 w-4"
                        style={{ color: "var(--mc-success-icon)" }}
                      />
                    </div>
                  </div>
                  <div>
                    {!hasChanges ? (
                      <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                        <span className="text-muted-foreground text-base">
                          —
                        </span>
                      </div>
                    ) : isPositiveCashFlow ? (
                      <div
                        className="text-2xl font-bold tracking-tight"
                        style={{ color: "var(--mc-success-icon)" }}
                      >
                        Positive Cash Flow
                      </div>
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                          {(() => {
                            const c = formatCoverageValue(
                              calculatedMetrics.scenarioRunway,
                            );
                            return <span title={c.tooltip}>{c.display}</span>;
                          })()}
                        </div>
                        {hasChanges && (
                          <div className="text-xs text-neutral-500 mt-0.5">
                            Base: {baseForecast.runway} months
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {hasChanges && !isPositiveCashFlow && (
                    <div className="flex items-center gap-1 pt-2 border-t">
                      {runwayChange >= 0 ? (
                        <TrendingUp
                          className="w-4 h-4"
                          style={{ color: "var(--mc-success-icon)" }}
                        />
                      ) : (
                        <TrendingDown
                          className="w-4 h-4"
                          style={{ color: "var(--mc-warning-icon)" }}
                        />
                      )}
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color:
                            runwayChange >= 0
                              ? "var(--mc-success-icon)"
                              : "var(--mc-warning-icon)",
                        }}
                      >
                        {runwayChange >= 0 ? "+" : ""}
                        {runwayChange} months
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Net Impact */}
              <Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
                <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-medium text-neutral-800">
                        Net Impact
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-64">
                            <p className="font-semibold mb-1">Net Impact</p>
                            <p className="text-xs">
                              The total cash difference this scenario creates
                              compared to your base forecast — positive means
                              more cash, negative means less.
                            </p>
                            <p className="text-xs mt-1 text-muted-foreground">
                              Calculation: Sum of all revenue adjustments minus
                              sum of all cost adjustments across the scenario
                              period.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--mc-info-bg)" }}
                    >
                      {hasChanges && calculatedMetrics.netImpact >= 0 ? (
                        <TrendingUp
                          className="h-4 w-4"
                          style={{ color: "var(--mc-info-icon)" }}
                        />
                      ) : (
                        <TrendingDown
                          className="h-4 w-4"
                          style={{ color: "var(--mc-info-icon)" }}
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                      {!hasChanges ? (
                        <span className="text-muted-foreground text-base">
                          —
                        </span>
                      ) : (
                        <span>
                          {calculatedMetrics.netImpact >= 0 ? "+" : ""}
                          {formatCurrency(calculatedMetrics.netImpact, "full")}
                        </span>
                      )}
                    </div>
                    {hasChanges && (
                      <div className="text-xs text-neutral-500 mt-0.5">
                        vs base forecast
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Min Cash */}
              <Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
                <CardContent className="p-4 flex flex-col justify-between h-full gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-medium text-neutral-800">
                        Min Cash
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-64">
                            <p className="font-semibold mb-1">Minimum Cash</p>
                            <p className="text-xs">
                              The lowest cash balance reached at any point
                              during the scenario period. This is your
                              worst-case cash position — critical for
                              identifying if you risk running short.
                            </p>
                            <p className="text-xs mt-1 text-muted-foreground">
                              The date shown is when this lowest point occurs.
                              Base figure is the minimum cash in the base
                              forecast for comparison.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "var(--mc-info-bg)" }}
                    >
                      <PoundSterling
                        className="h-4 w-4"
                        style={{ color: "var(--mc-info-icon)" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                      {!hasChanges ? (
                        <span className="text-muted-foreground text-base">
                          No changes yet
                        </span>
                      ) : (
                        formatCurrency(calculatedMetrics.minCash, "full")
                      )}
                    </div>
                    {hasChanges && (
                      <div className="text-xs text-neutral-500 mt-0.5">
                        Base: {formatCurrency(baseForecast.minCash, "full")} ·{" "}
                        {formatDate(new Date(calculatedMetrics.minCashDate))}
                      </div>
                    )}
                  </div>
                  {hasChanges && (
                    <div className="flex items-center gap-1 pt-2 border-t">
                      {minCashChange >= 0 ? (
                        <TrendingUp
                          className="w-4 h-4"
                          style={{ color: "var(--mc-success-icon)" }}
                        />
                      ) : (
                        <TrendingDown
                          className="w-4 h-4"
                          style={{ color: "var(--mc-warning-icon)" }}
                        />
                      )}
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color:
                            minCashChange >= 0
                              ? "var(--mc-success-icon)"
                              : "var(--mc-warning-icon)",
                        }}
                      >
                        {minCashChange >= 0 ? "+" : ""}
                        {formatCurrency(minCashChange, "full")}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="builder">
              Builder{adjustmentCount > 0 ? ` (${adjustmentCount})` : ""}
            </TabsTrigger>
            <TabsTrigger value="impact">Impact Summary</TabsTrigger>
            <TabsTrigger value="discussion">
              {(() => {
                const count =
                  mode === "live"
                    ? liveComments.length
                    : scenario.comments?.length || 0;
                return count > 0 ? `Discussion (${count})` : "Discussion";
              })()}
            </TabsTrigger>
            <TabsTrigger value="history">
              {(() => {
                const versions =
                  mode === "live" ? liveVersions : scenario.versions || [];
                return versions.length > 0
                  ? `History (v${Math.max(...versions.map((v) => v.version))})`
                  : "History";
              })()}
            </TabsTrigger>
            <TabsTrigger value="dependencies">
              {(() => {
                if (!scenario.dependencies) return "Dependencies";
                if (Array.isArray(scenario.dependencies)) {
                  return scenario.dependencies.length > 0
                    ? `Dependencies (${scenario.dependencies.length})`
                    : "Dependencies";
                }
                const count =
                  (scenario.dependencies.prerequisites?.length || 0) +
                  (scenario.dependencies.conflicts?.length || 0);
                return count > 0 ? `Dependencies (${count})` : "Dependencies";
              })()}
            </TabsTrigger>
            <TabsTrigger value="sharing">
              {(() => {
                if (!scenario.shares || scenario.shares.length === 0)
                  return "Sharing";
                const count = scenario.shares.filter(
                  (share: { userId?: string }) => share && share.userId,
                ).length;
                return count > 0 ? `Sharing (${count})` : "Sharing";
              })()}
            </TabsTrigger>
            <TabsTrigger value="issues" className="relative">
              {activeIssueCount > 0 ? (
                <span className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                  </span>
                  Issues ({activeIssueCount})
                </span>
              ) : (
                "Issues"
              )}
            </TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
            <TabsTrigger value="approvals">
              {(() => {
                const count = scenario.approvalHistory?.length ?? 0;
                return count > 0
                  ? `Approval History (${count})`
                  : "Approval History";
              })()}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="space-y-4">
            <ScenarioBuilder
              scenarioId={scenarioId}
              onAdjustmentCountChange={setAdjustmentCount}
              onLiveMetricsChange={setLiveMetrics}
              readOnly={!canEdit}
              refreshSignal={tableRefreshSignal}
            />
          </TabsContent>

          <TabsContent value="impact" className="space-y-4">
            {(() => {
              const isLive = mode === "live";

              // ── Live mode: derive everything from GraphQL data ──────────
              if (isLive) {
                const revenueNodes = liveAdjustmentNodes.filter(
                  (n) => n.adjustmentDirection === "inflow",
                );
                const costNodes = liveAdjustmentNodes.filter(
                  (n) => n.adjustmentDirection === "outflow",
                );

                const revenueNetCashImpact =
                  adjustmentTotals?.revenueNetCashImpact ??
                  revenueNodes.reduce(
                    (sum, n) => sum + (n.netCashImpact ?? 0),
                    0,
                  );
                // Cost net cash impact is signed (outflow → negative), so the
                // total is a straight sum, not a subtraction.
                const costNetCashImpact =
                  adjustmentTotals?.costNetCashImpact ??
                  costNodes.reduce((sum, n) => sum + (n.netCashImpact ?? 0), 0);
                const totalNetCashImpact =
                  revenueNetCashImpact + costNetCashImpact;

                const revenueCount =
                  adjustmentTotals?.revenueCount ?? revenueNodes.length;
                const costCount =
                  adjustmentTotals?.costCount ?? costNodes.length;

                // Type label for the badge
                const getAdjTypeLabel = (adjType: string): string => {
                  const map: Record<string, string> = {
                    percentage_adj: "% Adjustment",
                    transaction_adj: "Transaction Adj.",
                    new_transaction: "New Transaction",
                  };
                  return map[adjType] ?? adjType;
                };

                // Period description per node
                const getNodePeriodLabel = (
                  node: ScenarioAdjustmentViewNode,
                ): string => {
                  const start = node.startDate
                    ? new Date(node.startDate).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : null;
                  const end = node.endDate
                    ? new Date(node.endDate).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : null;
                  const freq =
                    node.frequency !== "one-off" ? node.frequency : null;
                  if (start && end)
                    return `${start} – ${end}${freq ? ` · ${freq}` : ""}`;
                  if (start) return `From ${start}${freq ? ` · ${freq}` : ""}`;
                  return freq
                    ? freq.charAt(0).toUpperCase() + freq.slice(1)
                    : "One-off";
                };

                const renderNodeRow = (node: ScenarioAdjustmentViewNode) => {
                  const cash = node.netCashImpact ?? 0;
                  const isPositive = cash >= 0;
                  return (
                    <div
                      key={node.id}
                      className="flex items-start justify-between p-3 border rounded-lg gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {getAdjTypeLabel(node.adjustmentType)}
                          </span>
                          <span className="font-medium text-sm truncate">
                            {node.description || "Adjustment"}
                          </span>
                        </div>
                        {node.additionalComments && (
                          <div className="text-xs text-muted-foreground truncate">
                            {node.additionalComments}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {getNodePeriodLabel(node)}
                        </div>
                      </div>
                      <div
                        className={`text-sm font-semibold shrink-0 ${isPositive ? "text-green-600" : "text-red-600"}`}
                      >
                        {isPositive ? "+" : ""}
                        {formatCurrency(cash, "full")}
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    {/* Adjustment Totals card */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Adjustment Totals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 border rounded-lg">
                            <div className="text-sm text-muted-foreground mb-1">
                              Revenue Adjustments
                            </div>
                            <div className="text-2xl font-bold text-green-600">
                              {revenueCount}
                            </div>
                            <div className="text-sm font-medium text-green-600 mt-1">
                              {revenueNetCashImpact >= 0 ? "+" : ""}
                              {formatCurrency(revenueNetCashImpact, "full")}
                            </div>
                          </div>
                          <div className="p-4 border rounded-lg">
                            <div className="text-sm text-muted-foreground mb-1">
                              Cost Adjustments
                            </div>
                            <div className="text-2xl font-bold text-red-600">
                              {costCount}
                            </div>
                            <div className="text-sm font-medium text-red-600 mt-1">
                              {costNetCashImpact >= 0 ? "+" : ""}
                              {formatCurrency(costNetCashImpact, "full")}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Impact Analysis card */}
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          {getScenarioTerm(true)} Impact Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Impact Breakdown */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">
                            Impact Breakdown
                          </h3>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <div className="font-medium">
                                  Revenue Changes
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {revenueCount} adjustment
                                  {revenueCount !== 1 ? "s" : ""}
                                </div>
                              </div>
                              <div
                                className={`text-lg font-semibold ${revenueNetCashImpact >= 0 ? "text-green-600" : "text-red-600"}`}
                              >
                                {revenueNetCashImpact >= 0 ? "+" : ""}
                                {formatCurrency(revenueNetCashImpact, "full")}
                              </div>
                            </div>
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                              <div>
                                <div className="font-medium">Cost Changes</div>
                                <div className="text-sm text-muted-foreground">
                                  {costCount} adjustment
                                  {costCount !== 1 ? "s" : ""}
                                </div>
                              </div>
                              <div
                                className={`text-lg font-semibold ${costNetCashImpact > 0 ? "text-green-600" : "text-red-600"}`}
                              >
                                {costNetCashImpact >= 0 ? "+" : ""}
                                {formatCurrency(costNetCashImpact, "full")}
                              </div>
                            </div>
                            <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                              <div>
                                <div className="font-medium">Net Impact</div>
                                <div className="text-sm text-muted-foreground">
                                  Total scenario effect
                                </div>
                              </div>
                              <div
                                className={`text-xl font-bold ${totalNetCashImpact >= 0 ? "text-green-600" : "text-red-600"}`}
                              >
                                {totalNetCashImpact >= 0 ? "+" : ""}
                                {formatCurrency(totalNetCashImpact, "full")}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Revenue adjustments list */}
                        <div>
                          <h3 className="text-lg font-semibold mb-3">
                            Revenue Adjustments ({revenueNodes.length})
                          </h3>
                          {revenueNodes.length > 0 ? (
                            <div className="space-y-2">
                              {revenueNodes.map(renderNodeRow)}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-3">
                              No revenue adjustments added
                            </p>
                          )}
                        </div>

                        {/* Cost adjustments list */}
                        <div>
                          <h3 className="text-lg font-semibold mb-3">
                            Cost Adjustments ({costNodes.length})
                          </h3>
                          {costNodes.length > 0 ? (
                            <div className="space-y-2">
                              {costNodes.map(renderNodeRow)}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-3">
                              No cost adjustments added
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              }

              // ── Demo mode: use scenario.changes + client-side engine ────
              const REVENUE_TYPES = new Set([
                "win-customer",
                "lose-customer",
                "add-revenue",
                "adjust-revenue",
                "change-revenue",
                "accelerate-payment",
              ]);
              const COST_TYPES = new Set([
                "hire-employees",
                "capital-purchase",
                "delay-payment",
                "change-costs",
                "add-cost",
                "adjust-cost",
              ]);

              const changes = scenario.changes || [];
              const revenueChanges = changes.filter((c) =>
                REVENUE_TYPES.has(c.type),
              );
              const costChanges = changes.filter((c) => COST_TYPES.has(c.type));
              const otherChanges = changes.filter(
                (c) => !REVENUE_TYPES.has(c.type) && !COST_TYPES.has(c.type),
              );

              const getCashImpact = (change: (typeof changes)[0]): number => {
                const d = change.details as Record<string, unknown>;
                if (typeof d?.netImpact === "number") return d.netImpact;
                if (typeof d?.monthlyImpact === "number")
                  return (d.monthlyImpact as number) * 3;
                if (typeof d?.adjustmentAmount === "number")
                  return d.adjustmentAmount as number;
                if (typeof d?.oneOffCost === "number")
                  return -(d.oneOffCost as number);
                if (typeof d?.annualCost === "number")
                  return -(((d.annualCost as number) / 12) * 3);
                if (
                  [
                    "adjust-revenue",
                    "adjust-cost",
                    "change-revenue",
                    "change-costs",
                  ].includes(change.type)
                )
                  return 0;
                return change.amount ?? 0;
              };

              const formatDateLabel = (value: unknown): string | null =>
                value
                  ? new Date(value as string).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : null;

              const getPeriodLabel = (change: (typeof changes)[0]): string => {
                const d = change.details as Record<string, unknown>;
                const freq = (d?.frequency ?? d?.recurrence) as
                  | string
                  | undefined;
                const freqLabel = freq
                  ? freq.charAt(0).toUpperCase() + freq.slice(1)
                  : null;

                if (
                  change.type === "adjust-revenue" ||
                  change.type === "adjust-cost"
                ) {
                  const modDate = formatDateLabel(
                    change.newDueDate ??
                      change.originalDueDate ??
                      change.effectiveDate,
                  );
                  return modDate
                    ? `Effective ${modDate}`
                    : (freqLabel ?? "One-off");
                }

                if (
                  change.type === "add-revenue" ||
                  change.type === "add-cost"
                ) {
                  const startDate = formatDateLabel(
                    change.effectiveDate ?? change.date,
                  );
                  if (startDate)
                    return `From ${startDate}${freqLabel ? ` · ${freqLabel}` : ""}`;
                  return freqLabel ?? "One-off";
                }

                const start = change.effectiveDate
                  ? new Date(change.effectiveDate).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : null;
                const end =
                  d?.endDate || change.endDate
                    ? new Date(
                        (d?.endDate || change.endDate) as string,
                      ).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : null;
                if (start && end)
                  return `${start} – ${end}${freq ? ` · ${freq}` : ""}`;
                if (start) return `From ${start}${freq ? ` · ${freq}` : ""}`;
                return freq
                  ? freq.charAt(0).toUpperCase() + freq.slice(1)
                  : "One-off";
              };

              const getTypeLabel = (type: string): string => {
                const map: Record<string, string> = {
                  "win-customer": "New Customer",
                  "lose-customer": "Lost Customer",
                  "add-revenue": "New Revenue",
                  "adjust-revenue": "Revenue %",
                  "change-revenue": "Revenue Adj.",
                  "accelerate-payment": "Payment In",
                  "hire-employees": "Headcount",
                  "capital-purchase": "Capital",
                  "delay-payment": "Payment Out",
                  "change-costs": "Cost Adj.",
                  "add-cost": "New Cost",
                  "adjust-cost": "Cost %",
                  custom: "Custom",
                };
                return map[type] ?? type;
              };

              const renderChangeRow = (change: (typeof changes)[0]) => {
                const cash = getCashImpact(change);
                const isPositive = cash >= 0;
                return (
                  <div
                    key={change.id}
                    className="flex items-start justify-between p-3 border rounded-lg gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {getTypeLabel(change.type)}
                        </span>
                        <span className="font-medium text-sm truncate">
                          {change.name}
                        </span>
                      </div>
                      {change.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {change.description}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {getPeriodLabel(change)}
                      </div>
                    </div>
                    <div
                      className={`text-sm font-semibold shrink-0 ${isPositive ? "text-green-600" : "text-red-600"}`}
                    >
                      {isPositive ? "+" : ""}
                      {formatCurrency(cash, "full")}
                    </div>
                  </div>
                );
              };

              return (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {getScenarioTerm(true)} Impact Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Summary totals */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">
                        Impact Breakdown
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium">Revenue Changes</div>
                            <div className="text-sm text-muted-foreground">
                              {revenueChanges.length} adjustment
                              {revenueChanges.length !== 1 ? "s" : ""}
                            </div>
                          </div>
                          <div
                            className={`text-lg font-semibold ${calculatedMetrics.revenueChangeAmount >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {calculatedMetrics.revenueChangeAmount >= 0
                              ? "+"
                              : ""}
                            {formatCurrency(
                              calculatedMetrics.revenueChangeAmount,
                              "full",
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium">Cost Changes</div>
                            <div className="text-sm text-muted-foreground">
                              {costChanges.length} adjustment
                              {costChanges.length !== 1 ? "s" : ""}
                            </div>
                          </div>
                          {/* costChangeAmount is the change in outflow (positive = more
													    cost); show it as a signed cash impact (a cost reduces cash). */}
                          {(() => {
                            const costCashImpact =
                              -calculatedMetrics.costChangeAmount;
                            return (
                              <div
                                className={`text-lg font-semibold ${costCashImpact > 0 ? "text-green-600" : "text-red-600"}`}
                              >
                                {costCashImpact > 0 ? "+" : ""}
                                {formatCurrency(costCashImpact, "full")}
                              </div>
                            );
                          })()}
                        </div>
                        {otherChanges.length > 0 && (
                          <div className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <div className="font-medium">
                                Other Adjustments
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {otherChanges.length} adjustment
                                {otherChanges.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                            <div className="text-lg font-semibold text-muted-foreground">
                              —
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                          <div>
                            <div className="font-medium">Net Impact</div>
                            <div className="text-sm text-muted-foreground">
                              Total scenario effect over 90 days
                            </div>
                          </div>
                          <div
                            className={`text-xl font-bold ${calculatedMetrics.netImpact >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {calculatedMetrics.netImpact >= 0 ? "+" : ""}
                            {formatCurrency(
                              calculatedMetrics.netImpact,
                              "full",
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Revenue adjustments detail */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Revenue Adjustments ({revenueChanges.length})
                      </h3>
                      {revenueChanges.length > 0 ? (
                        <div className="space-y-2">
                          {revenueChanges.map(renderChangeRow)}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-3">
                          No revenue adjustments added
                        </p>
                      )}
                    </div>

                    {/* Cost adjustments detail */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Cost Adjustments ({costChanges.length})
                      </h3>
                      {costChanges.length > 0 ? (
                        <div className="space-y-2">
                          {costChanges.map(renderChangeRow)}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground py-3">
                          No cost adjustments added
                        </p>
                      )}
                    </div>

                    {/* Other adjustments detail */}
                    {otherChanges.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">
                          Other Adjustments ({otherChanges.length})
                        </h3>
                        <div className="space-y-2">
                          {otherChanges.map(renderChangeRow)}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>

          <TabsContent value="discussion" className="space-y-4">
            <ChatDiscussion
              comments={
                mode === "live" ? liveComments : scenario.comments || []
              }
              currentUserId={
                mode === "live" && currentUserData?.id
                  ? currentUserData.id
                  : currentUser?.id || "unknown"
              }
              currentUserName={
                mode === "live" && currentUserData?.firstName
                  ? `${currentUserData.firstName} ${currentUserData.lastName ?? ""}`.trim()
                  : currentUser?.name || "Unknown User"
              }
              onPostComment={handlePostComment}
              readOnly={!canComment}
            />
          </TabsContent>

          <TabsContent value="dependencies" className="space-y-4">
            <DependenciesTab
              scenario={scenario}
              onUpdate={handleUpdate}
              readOnly={!canEdit}
            />
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Version History</CardTitle>
                  {canEdit && (
                    <Button
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      onClick={handleSaveVersion}
                    >
                      Save Version
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const displayVersions =
                    mode === "live" ? liveVersions : scenario.versions || [];
                  return displayVersions.length > 0 ? (
                    <div className="space-y-3">
                      {/* Sort versions in reverse chronological order (highest version number first) */}
                      {[...displayVersions]
                        .sort((a, b) => b.version - a.version)
                        .map((version, index) => {
                          // The highest version number is the current version
                          const isCurrentVersion = index === 0;

                          return (
                            <div
                              key={version.version}
                              className="border rounded-lg p-4"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="font-medium">
                                    v{version.version}
                                    {isCurrentVersion && (
                                      <Badge className="ml-2" variant="outline">
                                        Current
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground mt-1">
                                    {version.description}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {version.createdByName} •{" "}
                                    {formatDate(version.createdAt)}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setViewingVersion(version)}
                                  >
                                    View
                                  </Button>
                                  {canEdit && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        setRestoringVersion(version)
                                      }
                                    >
                                      Restore
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No version history available
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sharing" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Who has access</CardTitle>
              </CardHeader>
              <CardContent>
                {scenarioAccess.isLoading ? (
                  <p className="text-sm text-muted-foreground mb-4">
                    Loading access…
                  </p>
                ) : scenarioAccess.access.length > 0 ? (
                  <div className="space-y-3">
                    {scenarioAccess.access.map((entry) => {
                      const displayName = entry.userName || "Unknown User";
                      const initials =
                        displayName !== "Unknown User"
                          ? displayName
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                          : "?";
                      const permissionLabel = entry.isOwner
                        ? "Owner"
                        : entry.permission === "edit"
                          ? "Can edit"
                          : entry.permission === "comment"
                            ? "Can comment"
                            : "Can view";
                      // Only explicit shares are removable, and only by the owner.
                      // Inherited access is derived and never removed here — even
                      // when the same user also has an explicit share.
                      const canRemove =
                        isOwner && entry.isExplicitShare && !entry.isOwner;
                      return (
                        <div
                          key={entry.userId}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                              {initials}
                            </div>
                            <div>
                              <div className="font-medium">{displayName}</div>
                              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                                <span>{permissionLabel}</span>
                                {entry.isInherited && !entry.isOwner && (
                                  <Badge variant="secondary">Inherited</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          {entry.isOwner ? (
                            <Badge variant="outline">Owner</Badge>
                          ) : canRemove ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShareToRemove({
                                  userId: entry.userId,
                                  displayName,
                                });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">
                    Not shared with anyone yet
                  </p>
                )}

                {isOwner && hasPermission("create_scenarios") && (
                  <Button
                    onClick={() => setShareModalOpen(true)}
                    className="mt-4 w-full"
                    variant="outline"
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    Share Scenario
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="issues" className="space-y-4">
            <ScenarioIssuesTab
              issues={localIssues}
              currentUserId={currentUser?.id ?? ""}
              currentUserName={currentUser?.name ?? "Unknown"}
            />
          </TabsContent>

          <TabsContent value="audit" className="space-y-4">
            <ScenarioAuditLogTab
              entries={auditEntries}
              isLoading={auditLogLoading}
            />
          </TabsContent>

          <TabsContent value="approvals" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Approval History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {scenario.approvalHistory &&
                scenario.approvalHistory.length > 0 ? (
                  scenario.approvalHistory.map((approval) => (
                    <div
                      key={approval.id}
                      className="flex items-start gap-2 text-sm"
                    >
                      <Badge
                        variant="outline"
                        className={
                          approval.status === "approved"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : approval.status === "rejected"
                              ? "bg-red-50 text-red-700 border-red-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                        }
                      >
                        {approval.status}
                      </Badge>
                      <div>
                        <span className="font-medium">{approval.userName}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          &mdash; {approval.userRole}
                        </span>
                        {approval.comments && (
                          <p className="text-muted-foreground mt-0.5">
                            {approval.comments}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {formatDate(approval.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground py-3">
                    No approval history for this{" "}
                    {getScenarioTerm().toLowerCase()}.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      {shareModalOpen && (
        <ShareScenarioModal
          scenario={scenario}
          onClose={() => setShareModalOpen(false)}
        />
      )}
      {parkModalOpen && (
        <ParkScenarioModal
          scenario={scenario}
          onClose={() => setParkModalOpen(false)}
        />
      )}

      <QuickAdjustmentsModal
        open={quickAdjustmentsModalOpen}
        onOpenChange={setQuickAdjustmentsModalOpen}
        onSelectAdjustment={(adjustmentType, adjustmentData) => {
          if (adjustmentType === "delay_invoice" && adjustmentData) {
            // Add the adjustment to the scenario
            const newChange: ScenarioChange = {
              id: `change-${Date.now()}`,
              type: "delay-payment",
              name: `Delay Invoice #${adjustmentData.invoiceNumber}`,
              description: `${adjustmentData.customer} - Delay payment from ${format(
                adjustmentData.originalDueDate,
                "MMM d",
              )} to ${format(adjustmentData.newDueDate, "MMM d")}${
                adjustmentData.reason ? ` - ${adjustmentData.reason}` : ""
              }`,
              amount: adjustmentData.amount,
              date: adjustmentData.newDueDate,
              details: {
                customer: adjustmentData.customer,
                invoiceNumber: adjustmentData.invoiceNumber,
              },
              createdBy: "current-user",
              createdAt: new Date(),
            };

            updateScenario(scenario.id, {
              changes: [...scenario.changes, newChange],
            });
          }
        }}
      />

      <BusinessEventsModal
        open={businessEventsModalOpen}
        onOpenChange={setBusinessEventsModalOpen}
        onSelectEvent={(eventType, eventData) => {
          console.log("Selected event:", eventType, eventData);
        }}
      />

      {/* Version Viewer Modal */}
      {viewingVersion && (
        <VersionViewerModal
          version={viewingVersion}
          open={!!viewingVersion}
          onClose={() => setViewingVersion(null)}
        />
      )}

      {/* Remove Share Confirmation Dialog */}
      <AlertDialog
        open={!!shareToRemove}
        onOpenChange={() => setShareToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <strong>{shareToRemove?.displayName}</strong> from this scenario?
              They will no longer be able to access it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShareToRemove(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (shareToRemove) {
                  removeShare(scenario.id, shareToRemove.userId);
                  setShareToRemove(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Version Dialog */}
      {/* View Approvers Modal — detail view */}
      <ViewApproversModal
        open={viewApproversOpen}
        onClose={() => setViewApproversOpen(false)}
        scenario={scenario}
      />

      {restoringVersion && (
        <RestoreVersionDialog
          open={!!restoringVersion}
          onClose={() => setRestoringVersion(null)}
          onConfirm={() => handleRestoreVersion(restoringVersion)}
          versionNumber={restoringVersion.version}
          currentVersion={(() => {
            const versions =
              mode === "live" ? liveVersions : scenario.versions || [];
            return versions.length > 0
              ? Math.max(...versions.map((v) => v.version))
              : 1;
          })()}
        />
      )}
    </div>
  );
};
